/**
 * Die Abweichungskennzahl je Agent (Spec, User Stories 86 und 87).
 *
 * **Sie wird ab v1 erhoben, obwohl sie erst in v2 gebraucht wird** (ADR-0007): An
 * ihr soll abgelesen werden, wann ein Agent nach vorn treten darf, und dafür
 * braucht sie im Moment ihrer Verwendung eine Historie.
 *
 * **Gerechnet, nicht gespeichert.** Deshalb stehen hier alle Zählungen und nicht
 * nur die Quote: Wer die Zahl liest, soll sie nachrechnen können — und eine
 * spätere, andere Formel entwertet die Reihe nicht, weil sie über denselben
 * Zuordnungen läuft.
 *
 * Sichtbar für `management` und `admin`. Dass ein Entwickler sie nicht bekommt,
 * entscheidet der Server bei jeder Anfrage; hier steht nur, wer den Bereich
 * angeboten bekommt.
 *
 * **Ohne Werkzeugleiste** (#41, #136): Sie zeigt an und bietet nichts zum
 * Handeln — kein Filter, kein Blättern, kein Knopf auf den Screen. Ein leerer
 * Glasstreifen ist schlechter als keiner
 * (`docs/human/2026-08-27-werkzeugleiste-und-sektionen.md`).
 */

import type { ReactElement } from "react";

import { Chip, DataTable, KpiTile } from "../design-system";
import type { Deviation, DeviationApi } from "../api/client";
import { formatNumber, formatShare } from "../formatting";
import { NumericCell, StrongCell } from "./cells";
import { ListPage, ListSection } from "./list";
import { Notice, useLoaded } from "./loading";

const EXPLANATION =
  "Übereinstimmung heißt: gleicher Pfad, gleiche Revision, mindestens eine gemeinsame " +
  "Zeile. Befundart und Schweregrad gehen nicht in den Treffer ein, werden aber je " +
  "Zuordnung mitgeschrieben. Aggregiert wird über alle Befunde eines Agenten, nicht je " +
  "Revision gemittelt. Revisionen, in denen beide Seiten nichts fanden, stehen getrennt " +
  "und gehen nicht in die Quote ein.";

export function DeviationView({ client }: { client: DeviationApi }): ReactElement {
  const { state } = useLoaded(() => client.deviations());

  const deviations = state.kind === "loaded" ? state.value : [];

  return (
    <ListPage>
      <ListSection
        title="Abweichung Mensch/Maschine"
        glyph="gauge"
        first
        headerRight={
          state.kind === "loaded" ? (
            <Chip>{`${formatNumber(deviations.length)} Agenten`}</Chip>
          ) : null
        }
      >
        <div style={{ padding: "0 var(--sp-5)" }}>
          {state.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
          {state.kind === "failed" ? (
            <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
          ) : null}
          {state.kind === "loaded" && deviations.length === 0 ? (
            <Notice text="Noch ist kein Agent angelegt." />
          ) : null}
        </div>

        <p
          style={{
            margin: 0,
            padding: "0 var(--sp-5) var(--sp-5)",
            maxWidth: "78ch",
            fontSize: "var(--fs-11)",
            lineHeight: "var(--lh-normal)",
            color: "var(--fg-6)",
            textWrap: "pretty",
          }}
        >
          {EXPLANATION}
        </p>

        {deviations.length === 0 ? null : (
          <DataTable
            columns={[
              { key: "agent", label: "Agent", maxWidth: 220 },
              { key: "deviation", label: "Abweichung", width: 120, align: "right" },
              { key: "matched", label: "Übereinstimmend", width: 130, align: "right" },
              { key: "missed", label: "Übersehen", width: 110, align: "right" },
              { key: "unmatched", label: "Nur Agent", width: 110, align: "right" },
              // „Fehlalarm" und nicht „verworfen": Das Wort gehört dem
              // Lebenszyklus (`dismissed`) und sagt nichts über die Maschine
              // aus — die Kennzahl liest allein die Qualifizierung (ADR-0017).
              { key: "rejected", label: "Fehlalarm", width: 110, align: "right" },
              { key: "confirmed", label: "Bestätigt", width: 110, align: "right" },
              { key: "bothEmpty", label: "Beide leer", width: 110, align: "right" },
            ]}
            rows={deviations.map((one) => ({
              cells: {
                agent: <StrongCell>{one.agentName}</StrongCell>,
                deviation: <NumericCell>{formatShare(one.deviation)}</NumericCell>,
                matched: <NumericCell>{formatNumber(one.matched)}</NumericCell>,
                missed: <NumericCell>{formatNumber(one.missed)}</NumericCell>,
                unmatched: <NumericCell>{formatNumber(one.unmatched)}</NumericCell>,
                rejected: <NumericCell>{formatNumber(one.rejected)}</NumericCell>,
                confirmed: <NumericCell>{formatNumber(one.confirmed)}</NumericCell>,
                bothEmpty: <NumericCell>{formatNumber(one.bothEmpty)}</NumericCell>,
              },
            }))}
          />
        )}
      </ListSection>

      {deviations.length === 0 ? null : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--sp-6)",
            padding: "var(--sp-6) var(--sp-5)",
          }}
        >
          {deviations.map((one) => (
            <AgentTiles key={one.agentId} deviation={one} />
          ))}
        </div>
      )}
    </ListPage>
  );
}

/**
 * Die Kennzahl eines Agenten als Kacheln.
 *
 * Fehlalarm und Übersehenes stehen getrennt: Beides ist Abweichung, für die Frage
 * nach dem Vortritt bedeuten sie aber Verschiedenes — ein Agent, der zu viel
 * meldet, ist etwas anderes als einer, der zu wenig sieht.
 */
function AgentTiles({ deviation }: { deviation: Deviation }): ReactElement {
  return (
    <section
      aria-label={`Kennzahl von ${deviation.agentName}`}
      style={{
        display: "grid",
        // Fünf Spalten, nicht die vier der Vorlage (`KpiTile.prompt.md`, „the
        // dashboard's four-up row"): Übersehen, Fehlalarm, Befundart/Schweregrad
        // und „beide ohne Befund" stehen neben der Quote selbst, und keine der
        // fünf entfällt, um auf die Vier der Vorlage zu kommen.
        gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
        // Die Trennung entsteht aus den 1-px-Fugen des Rasters und nicht aus
        // einem Abstand zwischen fünf eigenen Rahmen — wörtlich wie die Vorlage
        // (`design/reference/DashboardScreen.jsx`), seit die Kennzahl eine Zelle
        // in einer Reihe ist und keine Karte mehr (#151).
        gap: 1,
        background: "var(--line-f)",
        borderTop: "1px solid var(--line-f)",
      }}
    >
      <KpiTile
        label={deviation.agentName}
        value={withMeta(formatShare(deviation.deviation), `über ${formatNumber(deviation.findings)} Zuordnungen`)}
      />
      <KpiTile
        label="Übersehen"
        value={withMeta(formatNumber(deviation.missed), "vom Menschen gesehen, vom Agenten nicht")}
      />
      <KpiTile
        label="Fehlalarm"
        value={withMeta(
          formatNumber(deviation.rejected),
          "vom Agenten gemeldet, vom Menschen als Fehlalarm bewertet",
        )}
      />
      <KpiTile
        label="Gleiche Befundart"
        value={withMeta(
          `${formatNumber(deviation.kindMatches)} / ${formatNumber(deviation.matched)}`,
          `gleicher Schweregrad: ${formatNumber(deviation.severityMatches)}`,
        )}
      />
      <KpiTile
        label="Beide ohne Befund"
        value={withMeta(formatNumber(deviation.bothEmpty), "Revisionen außerhalb der Quote")}
      />
    </section>
  );
}

/**
 * Der Wert einer Kennzahl mit seiner Erläuterung darunter.
 *
 * `KpiTile` führt kein `meta` mehr: Sein `delta` ist eine grüne Pille für eine
 * **Veränderung**, und keiner dieser fünf Texte ist eine — „vom Menschen gesehen,
 * vom Agenten nicht" ist eine Auskunft darüber, was gezählt wurde. Der Text zieht
 * deshalb als zweite Zeile in den Wert, statt eine Veränderung zu behaupten oder
 * verloren zu gehen (#151).
 */
function withMeta(value: string, meta: string): ReactElement {
  return (
    <>
      {value}
      <span
        style={{
          display: "block",
          fontSize: "var(--fs-11)",
          fontWeight: "var(--fw-regular)",
          color: "var(--fg-8)",
        }}
      >
        {meta}
      </span>
    </>
  );
}
